import type { PhotoDimensions, PhotoRect } from '@repo/design-renderer';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { type FlatFitMode, fitSpaceView, type SpaceFit } from './flat-mode-mapping';

/** 화면 상자 가장자리 여백(CSS px). */
export const CANVAS_PADDING_PX = 24;
/** 영역 확대에서 허용하는 배율 — 전체 보기 배율의 몇 배까지. */
const MIN_ZOOM_OF_FIT = 0.5;
const MAX_ZOOM_OF_FIT = 8;
/** 이만큼 안 움직이고 떼면 끌기가 아니라 클릭이다. */
const PAN_THRESHOLD_PX = 3;

/** 빈 곳 끌기 — 움직이면 화면 이동, 안 움직이고 떼면 누른 자리의 클릭(payload)으로 본다. */
interface Pan<T> {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly left: number;
  readonly top: number;
  readonly payload: T;
  readonly moved: boolean;
}

interface UseCanvasViewportInput {
  /** 공유 공간 크기 — 사진 px 또는 가상 mm 공간. */
  readonly space: PhotoDimensions;
  /** 인쇄 영역들의 외접 사각형(공간 좌표). 없으면 공간 전체. */
  readonly focus: PhotoRect | null;
  readonly fitMode: FlatFitMode;
  readonly onFitModeChange?: (mode: FlatFitMode) => void;
}

interface PointerLike {
  readonly pointerId: number;
  readonly clientX: number;
  readonly clientY: number;
}

export interface CanvasViewport<T> {
  readonly hostRef: RefObject<HTMLDivElement | null>;
  /** 캔버스 화면 크기(CSS px). */
  readonly size: { readonly width: number; readonly height: number };
  /** 지금 배율·위치(전체 보기·영역 확대 자동 맞춤 또는 사용자가 휠·끌기로 바꾼 값). */
  readonly fit: SpaceFit;
  /** 화면 위 공간(사진) 상자(CSS px, 상자 기준). */
  readonly photoBox: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
  };
  readonly hostRect: () => { readonly left: number; readonly top: number };
  readonly capture: (pointerId: number) => void;
  readonly release: (pointerId: number) => void;
  /** 빈 곳 끌기를 시작한다 — payload 는 떼었을 때 안 움직였으면 돌려받는 클릭 정보. */
  readonly startPan: (event: PointerLike, payload: T) => void;
  /** 끌기 중이면 화면을 옮기고 true. 끌기 중이 아니면 false. */
  readonly movePan: (event: { readonly clientX: number; readonly clientY: number }) => boolean;
  /** 끌기를 끝낸다. 끌기 중이 아니었으면 null. */
  readonly finishPan: () => { readonly payload: T; readonly moved: boolean } | null;
  readonly cancelPan: () => void;
  /** 끌기 중이고 움직였는지 — 커서 모양. */
  readonly panMoved: boolean;
}

/**
 * 편집 캔버스의 보기 상태 — 상자 크기(ResizeObserver), 전체 보기/영역 확대 자동 맞춤, 휠 확대(전체 보기에서 굴리면
 * 영역 확대로 넘어간다), 영역 확대에서 빈 곳 끌어 옮기기, 포인터 캡처. 평면·휜 편집 캔버스가 같이 쓴다.
 */
export const useCanvasViewport = <T>({
  space,
  focus,
  fitMode,
  onFitModeChange,
}: UseCanvasViewportInput): CanvasViewport<T> => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 400, height: 300 });
  // 영역 확대 모드의 사용자 배율·위치. null 이면 그 모드의 자동 맞춤을 쓴다(모드에 처음 들어왔을 때).
  const [free, setFree] = useState<SpaceFit | null>(null);
  const [pan, setPan] = useState<Pan<T> | null>(null);

  // 사진이 없으면 가상 공간이 곧 영역들의 외접 사각형이라 전체 보기로 꽉 채운다.
  const target = focus ?? { x: 0, y: 0, width: space.widthPx, height: space.heightPx };
  const wholeFit = fitSpaceView(size, space, target, CANVAS_PADDING_PX, 'photo');
  const autoFit =
    fitMode === 'area' ? fitSpaceView(size, space, target, CANVAS_PADDING_PX, 'area') : wholeFit;
  const fit = fitMode === 'area' && free !== null ? free : autoFit;
  const photoBox = {
    left: fit.left,
    top: fit.top,
    width: space.widthPx * fit.k,
    height: space.heightPx * fit.k,
  };

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (fitMode !== 'area') setFree(null);
  }, [fitMode]);

  /** 화면 한 점을 고정한 채 배율을 바꾼다 — 전체 보기 배율의 0.5~8배로 묶는다. */
  const zoomAt = (base: SpaceFit, factor: number, cx: number, cy: number): SpaceFit => {
    const k = Math.min(
      wholeFit.k * MAX_ZOOM_OF_FIT,
      Math.max(wholeFit.k * MIN_ZOOM_OF_FIT, base.k * factor),
    );
    const ratio = k / base.k;
    return { k, left: cx - (cx - base.left) * ratio, top: cy - (cy - base.top) * ratio };
  };

  // React 의 onWheel 은 passive 라 페이지 스크롤을 못 막는다 — 직접 붙인다. 최신 fit·모드를 클로저로 읽도록 매 렌더 다시 단다.
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = host.getBoundingClientRect();
      const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0015));
      setFree(zoomAt(fit, factor, event.clientX - rect.left, event.clientY - rect.top));
      if (fitMode !== 'area') onFitModeChange?.('area');
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => host.removeEventListener('wheel', onWheel);
  });

  const hostRect = () => hostRef.current?.getBoundingClientRect() ?? { left: 0, top: 0 };

  const capture = (pointerId: number) => {
    const host = hostRef.current;
    if (host !== null && typeof host.setPointerCapture === 'function')
      host.setPointerCapture(pointerId);
  };

  const release = (pointerId: number) => {
    const host = hostRef.current;
    if (
      host !== null &&
      typeof host.hasPointerCapture === 'function' &&
      host.hasPointerCapture(pointerId)
    )
      host.releasePointerCapture(pointerId);
  };

  const startPan = (event: PointerLike, payload: T) => {
    setPan({
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      left: fit.left,
      top: fit.top,
      payload,
      moved: false,
    });
  };

  const movePan = (event: { readonly clientX: number; readonly clientY: number }): boolean => {
    if (pan === null) return false;
    const dx = event.clientX - pan.startX;
    const dy = event.clientY - pan.startY;
    const moved = pan.moved || Math.hypot(dx, dy) > PAN_THRESHOLD_PX;
    if (moved) {
      setFree({ k: fit.k, left: pan.left + dx, top: pan.top + dy });
      if (!pan.moved) setPan({ ...pan, moved: true });
    }
    return true;
  };

  const finishPan = () => {
    if (pan === null) return null;
    setPan(null);
    return { payload: pan.payload, moved: pan.moved };
  };

  return {
    hostRef,
    size,
    fit,
    photoBox,
    hostRect,
    capture,
    release,
    startPan,
    movePan,
    finishPan,
    cancelPan: () => setPan(null),
    panMoved: pan?.moved ?? false,
  };
};
